import React, { useState, useEffect, lazy, Suspense } from 'react';
import '@/App.css';
import clarity from '@microsoft/clarity';
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { GeoRedirectGuard } from '@/components/locations/GeoRedirectGuard';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from '@/components/ui/sonner';
import { useTranslation } from 'react-i18next';
import LanguageToggle from '@/components/LanguageToggle';
import LanguagePrompt from '@/components/LanguagePrompt';
import HreflangTags from '@/components/seo/HreflangTags';
import RouteSEO from '@/components/RouteSEO';
import StructuredData from '@/components/StructuredData';
import { EN_TO_ES as EN_TO_ES_MAP } from '@/utils/languageRoutes';
import { Menu, X, ChevronDown, Search, DollarSign, Sparkles, Eye, Code, Bot, GraduationCap, Monitor, Shield, Car, Store, Smartphone, Building, Trophy, Heart, Wrench, Zap, Droplets, Leaf, Bug, Flame, HardHat, Link2, Users, MapPin, Phone, MessageSquare } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
const DealerSection = lazy(() => import('@/components/DealerSection'));
const MobileAppsSection = lazy(() => import('@/components/MobileAppsSection'));
const AISearchSection = lazy(() => import('@/components/AISearchSection'));
const PersonalPortfolioSection = lazy(() => import('@/components/PersonalPortfolioSection'));
const PortfolioSuccess = lazy(() => import('@/components/PortfolioSuccess'));
const DomainSuccess = lazy(() => import('@/components/DomainSuccess'));
const AffiliateLanding = lazy(() => import('@/components/AffiliateLanding'));
const ToolsPortal = lazy(() => import('@/components/ToolsPortal'));
const SEOIntelligenceLanding = lazy(() => import('@/components/seo/SEOIntelligenceLanding'));
import RefAttributionCapture from '@/components/RefAttributionCapture';
const WarmHandoffPlaybook = lazy(() => import('@/components/WarmHandoffPlaybook'));
const BecomeAffiliatePage = lazy(() => import('@/components/BecomeAffiliatePage'));
const AffiliateSimpleLanding = lazy(() => import('@/components/AffiliateSimpleLanding'));
const AffiliatePortfolioLanding = lazy(() => import('@/components/AffiliatePortfolioLanding'));
const AffiliatePromoPage = lazy(() => import('@/components/AffiliatePromoPage'));
const AffiliateRecruitPage = lazy(() => import('@/components/AffiliateRecruitPage'));
const InfluencerVideoCard = lazy(() => import('@/components/InfluencerVideoCard'));
const InfluencerLanding = lazy(() => import('@/components/InfluencerLanding'));
const AffiliateDashboard = lazy(() => import('@/components/AffiliateDashboard'));
const SisterSiteMarketingKit = lazy(() => import('@/components/affiliate/SisterSiteMarketingKit'));
const SisterSitePartnerLanding = lazy(() => import('@/components/affiliate/SisterSitePartnerLanding'));
const AutoPostSetupGuide = lazy(() => import('@/components/affiliate/AutoPostSetupGuide'));
const PartnerLinkedInQueue = lazy(() => import('@/components/PartnerLinkedInQueue'));
const PartnerMissedEmails = lazy(() => import('@/components/PartnerMissedEmails'));
const DontScanLanding = lazy(() => import('@/components/DontScanLanding'));
const AiSearchAudit = lazy(() => import('@/pages/AiSearchAudit'));
const ClientIntake = lazy(() => import('@/pages/ClientIntake'));
const SecureFindingsPage = lazy(() => import('@/components/SecureFindingsPage'));
const NetworkAuditPage = lazy(() => import('@/components/NetworkAuditPage'));
const OnboardingPage = lazy(() => import('@/components/OnboardingPage'));
const OfferPage = lazy(() => import('@/components/OfferPage'));
const StandaloneProposalPage = lazy(() => import('@/components/StandaloneProposalPage'));
const PartnerInstallLanding = lazy(() => import('@/components/PartnerInstallLanding'));
const PublicTicketPage = lazy(() => import('@/pages/PublicTicketPage'));
const SpanishIndexPage = lazy(() => import('@/components/SpanishIndexPage'));
const SpanishHome = lazy(() => import('@/components/SpanishHome'));
const SpanishLandingPage = lazy(() => import('@/components/SpanishLandingPage'));
const RedesignMockupView = lazy(() => import('@/components/RedesignMockupView'));
const SitePage = lazy(() => import('@/components/SitePage'));
const SiteLaunchPage = lazy(() => import('@/components/SiteLaunchPage'));
const LrBostonMockup = lazy(() => import('@/components/LrBostonMockup'));
const LrBostonProposal = lazy(() => import('@/components/LrBostonProposal'));
const CodeGate = lazy(() => import('@/components/CodeGate'));
const SpanishBecomePartnerPage = lazy(() => import('@/components/SpanishBecomePartnerPage'));
const SpanishPartnerLanding = lazy(() => import('@/components/SpanishPartnerLanding'));
const SpanishPartnerTerms = lazy(() => import('@/components/SpanishPartnerTerms'));
const SpanishPartnerPortfolio = lazy(() => import('@/components/SpanishPartnerPortfolio'));
const DriverApp = lazy(() => import('@/components/driver/DriverApp'));
const AdminDriveCoverage = lazy(() => import('@/components/AdminDriveCoverage'));
const BilingualAddonLanding = lazy(() => import('@/components/BilingualAddonLanding'));
const DerbyTipsheetLanding = lazy(() => import('@/components/DerbyTipsheetLanding'));
const DerbyTipsheetSpanish = lazy(() => import('@/components/DerbyTipsheetSpanish'));
const LonestarTipsheetLanding = lazy(() => import('@/components/LonestarTipsheetLanding'));
const LonestarTipsheetSpanish = lazy(() => import('@/components/LonestarTipsheetSpanish'));
const PreaknessTipsheetLanding = lazy(() => import('@/components/PreaknessTipsheetLanding'));
const PreaknessTipsheetSpanish = lazy(() => import('@/components/PreaknessTipsheetSpanish'));
const BelmontTipsheetLanding = lazy(() => import('@/components/BelmontTipsheetLanding'));
const BelmontTipsheetSpanish = lazy(() => import('@/components/BelmontTipsheetSpanish'));
const ResultsPage = lazy(() => import('@/pages/ResultsPage'));
const MapDominatorLanding = lazy(() => import('@/components/MapDominatorLanding'));
const CharityFundraiserLanding = lazy(() => import('@/components/CharityFundraiserLanding'));
const ProposalPage = lazy(() => import('@/pages/ProposalPage'));
const ClientLsaDashboard = lazy(() => import('@/pages/ClientLsaDashboard'));
const LsaWelcome = lazy(() => import('@/pages/LsaWelcome'));
const SocialOnboarding = lazy(() => import('@/pages/SocialOnboarding'));
const MapScanWizard = lazy(() => import('@/components/MapScanWizard'));
const SpanishMapScan = lazy(() => import('@/components/SpanishMapScan'));
const WarmHandoffsLanding = lazy(() => import('@/components/WarmHandoffsLanding'));
const DataAccessOnboarding = lazy(() => import('@/pages/DataAccessOnboarding'));
const SuperAffiliateLanding = lazy(() => import('@/components/SuperAffiliateLanding'));
const UltraAffiliateDashboard = lazy(() => import('@/components/UltraAffiliateDashboard'));
const AffiliateLeaderboard = lazy(() => import('@/components/AffiliateLeaderboard'));
const AcceptInvitePage = lazy(() => import('@/components/client_portal/AcceptInvitePage'));
const ClientPortalTeam = lazy(() => import('@/components/client_portal/ClientPortalTeam'));
const ClientPortalLogin = lazy(() => import('@/components/client_portal/ClientPortalLogin'));
const PartnersLandingPage = lazy(() => import('@/components/PartnersLandingPage'));
const MediaKit = lazy(() => import('@/components/MediaKit'));
const AffiliateStatus = lazy(() => import('@/components/AffiliateStatus'));
const AdminLogin = lazy(() => import('@/components/AdminLogin'));
const AdminDashboard = lazy(() => import('@/components/AdminDashboard'));
import HomePage from '@/components/HomePage';
const ConsultationSimple = lazy(() => import('@/components/ConsultationSimple'));
const AIVisibilityTrial = lazy(() => import('@/components/AIVisibilityTrial'));
const AIVisibilitySuccess = lazy(() => import('@/components/AIVisibilitySuccess'));
const AIVisibilityCheckerPage = lazy(() => import('@/components/AIVisibilityCheckerPage'));
const AIVisibilityDemo = lazy(() => import('@/components/AIVisibilityDemo'));
const AIVisibilityReport2026 = lazy(() => import('@/components/AIVisibilityReport2026'));
import AIChatWidget from '@/components/AIChatWidget';
const LocalBusinessLanding = lazy(() => import('@/components/LocalBusinessLanding'));
const SEOTurboLanding = lazy(() => import('@/components/SEOTurboLanding'));
const SEOTurboSuccess = lazy(() => import('@/components/SEOTurboSuccess'));
const SEODashboard = lazy(() => import('@/components/SEODashboard'));
const CalendlyBooking = lazy(() => import('@/components/CalendlyBooking'));
const FloatingCalendlyButton = lazy(() => import('@/components/FloatingCalendlyButton'));
const BlogList = lazy(() => import('@/components/BlogList'));
const BlogPost = lazy(() => import('@/components/BlogPost'));
const SpanishBlogList = lazy(() => import('@/components/SpanishBlogList'));
const SpanishBlogPost = lazy(() => import('@/components/SpanishBlogPost'));
const SocialMediaHub = lazy(() => import('@/components/SocialMediaHub'));
const SocialMediaPricing = lazy(() => import('@/components/SocialMediaPricing'));
const SocialMediaLanding = lazy(() => import('@/components/SocialMediaLanding'));
const ClientSocialPortal = lazy(() => import('@/components/ClientSocialPortal'));
const GiftOnboardPage = lazy(() => import('@/components/GiftOnboardPage'));
const AgencyRegisterPage = lazy(() => import('@/components/AgencyRegisterPage'));
const AgencyDashboard = lazy(() => import('@/components/AgencyDashboard'));
const AgencyClientPortal = lazy(() => import('@/components/AgencyClientPortal'));
const AgencyScanner = lazy(() => import('@/components/AgencyScanner'));
const GiftPortfolioLanding = lazy(() => import('@/components/GiftPortfolioLanding'));
const PressReleaseArchive = lazy(() => import('@/components/PressReleaseArchive'));
const PressReleaseDetail = lazy(() => import('@/components/PressReleaseDetail'));
const PressPage = lazy(() => import('@/components/PressPage'));
const CampaignWebsitesPage = lazy(() => import('@/components/CampaignWebsitesPage'));
const EmailConfirmation = lazy(() => import('@/components/EmailConfirmation'));
const DallasFortWorth = lazy(() => import('@/components/locations/DallasFortWorth'));
const Austin = lazy(() => import('@/components/locations/Austin'));
const Houston = lazy(() => import('@/components/locations/Houston'));
const SanAntonio = lazy(() => import('@/components/locations/SanAntonio'));
const Arlington = lazy(() => import('@/components/locations/Arlington'));
const CityLandingPage = lazy(() => import('@/components/locations/CityLandingPage'));
const ServiceLocationPage = lazy(() => import('@/components/services/ServiceLocationPage'));
const DentalImplantsSEOFortWorth = lazy(() => import('@/components/industries/DentalImplantsSEOFortWorth'));
import { WebDesignArlington, SEOArlington, EcommerceArlington } from '@/components/services';
const SEOPackagePage = lazy(() => import('@/components/SEOPackagePage'));
const SeoAutopilotConsole = lazy(() => import('@/components/SeoAutopilotConsole'));
const AdminHaroAssistant = lazy(() => import('@/pages/AdminHaroAssistant'));
const GoLinks = lazy(() => import('@/components/GoLinks'));
const ProposalView = lazy(() => import('@/components/portfolio/ProposalView'));
const PortfolioOwnerDashboard = lazy(() => import('@/components/portfolio/PortfolioOwnerDashboard'));
const ContestEndedPage = lazy(() => import('@/components/ContestEndedPage'));
const ContestSocialPage = lazy(() => import('@/components/ContestSocialPage'));
const ContestVoting = lazy(() => import('@/components/ContestVoting'));
const ContestVerify = lazy(() => import('@/components/ContestVerify'));
const ContestGiftForm = lazy(() => import('@/components/ContestGiftForm'));
const ShareToolsPage = lazy(() => import('@/components/ShareToolsPage'));
const Contest2ShareTools = lazy(() => import('@/components/Contest2ShareTools'));
const GrandPrizePage = lazy(() => import('@/components/GrandPrizePage'));
const ContestWinnersPage = lazy(() => import('@/components/ContestWinnersPage'));
const ContestThankYou = lazy(() => import('@/components/ContestThankYou'));
const WinnerOnboardingForm = lazy(() => import('@/components/WinnerOnboardingForm'));
const RunnerUpOnboardingForm = lazy(() => import('@/components/RunnerUpOnboardingForm'));
const ServiceOnboarding = lazy(() => import('@/components/ServiceOnboarding'));
const ServicePaymentSuccess = lazy(() => import('@/components/ServicePaymentSuccess'));
const DealerLandingPage = lazy(() => import('@/components/DealerLandingPage'));
const PricingPackages = lazy(() => import('@/components/PricingPackages'));
const CLAuctionsLanding = lazy(() => import('@/components/CLAuctionsLanding'));
const FileReupload = lazy(() => import('@/components/FileReupload'));
const ProspectFileUpload = lazy(() => import('@/components/ProspectFileUpload'));
const EnterpriseOnboarding = lazy(() => import('@/components/EnterpriseOnboarding'));
const ClientCredentialsForm = lazy(() => import('@/components/ClientCredentialsForm'));
const PrivacyPolicy = lazy(() => import('@/components/PrivacyPolicy'));
const TermsOfService = lazy(() => import('@/components/TermsOfService'));
const SMSTerms = lazy(() => import('@/components/SMSTerms'));
const GoogleAdsAPITerms = lazy(() => import('@/components/GoogleAdsAPITerms'));
const PPCAuditPage = lazy(() => import('@/components/PPCAuditPage'));
import AdminFloatingButton from '@/components/AdminFloatingButton';
import TextUsWidget from '@/components/TextUsWidget';
import SmartPopup from '@/components/SmartPopup';
const ContestAnnouncementBar = lazy(() => import('@/components/ContestAnnouncementBar'));
const ContestEntryPopup = lazy(() => import('@/components/ContestEntryPopup'));
const FAQPage = lazy(() => import('@/components/FAQPage'));
const NotFoundPage = lazy(() => import('@/components/NotFoundPage'));
const UnsubscribePage = lazy(() => import('@/pages/UnsubscribePage'));
import SchemaStack from '@/components/SchemaStack';
const HostingPaymentPage = lazy(() => import('@/pages/HostingPaymentPage'));
const AboutUs = lazy(() => import('@/components/AboutUs'));
const VideoGallery = lazy(() => import('@/components/VideoGallery'));
const EcAcademy = lazy(() => import('@/components/EcAcademy'));
const BacklinksPage = lazy(() => import('@/components/BacklinksPage'));
const VisitorIdOffer = lazy(() => import('@/components/VisitorIdOffer'));
const VisitorLeadsLanding = lazy(() => import('@/components/VisitorLeadsLanding'));
const PortfolioPage = lazy(() => import('@/components/PortfolioPage'));
const AffiliateTerms = lazy(() => import('@/components/AffiliateTerms'));
const AdminPasswordReset = lazy(() => import('@/components/AdminPasswordReset'));
const AffiliatePasswordReset = lazy(() => import('@/components/AffiliatePasswordReset'));
const NewBusinessOffer = lazy(() => import('@/components/NewBusinessOffer'));
const UnlockAccount = lazy(() => import('@/components/UnlockAccount'));
const ReviewUsPage = lazy(() => import('@/components/ReviewUsPage'));
import ExitIntentPopup from '@/components/ExitIntentPopup';
import DynamicPopup from '@/components/DynamicPopup';
import CallbackWidget from '@/components/CallbackWidget';
import { usePopupsPaused, startPopupPausePolling } from '@/utils/popupPause';
const MetaAppReviewAccelerator = lazy(() => import('@/components/MetaAppReviewAccelerator'));
const MetaReviewTool = lazy(() => import('@/components/MetaReviewTool'));
const MetaReviewPortal = lazy(() => import('@/components/MetaReviewPortal'));
const PPCManagementFunnel = lazy(() => import('@/components/PPCManagementFunnel'));
const PPCClientPortal = lazy(() => import('@/components/PPCClientPortal'));
import Footer from '@/components/Footer';
const DynamicPaymentPage = lazy(() => import('@/pages/DynamicPaymentPage'));
const SuperpixelReport = lazy(() => import('@/pages/SuperpixelReport'));
const SeoAuditReport = lazy(() => import('@/pages/SeoAuditReport'));
const DigitalMarketingPlatform = lazy(() => import('@/pages/DigitalMarketingPlatform'));
const WebDesignServicesPage = lazy(() => import('@/pages/WebDesignServicesPage'));
const WebsiteAnalysisPage = lazy(() => import('@/pages/WebsiteAnalysisPage'));
const MockDemo = lazy(() => import('@/pages/MockDemo'));
const ClientLogin = lazy(() => import('@/components/client-dashboard/ClientLogin'));
const ClientDashboard = lazy(() => import('@/components/client-dashboard/ClientDashboard'));
const ClientOnboardingForm = lazy(() => import('@/components/client-dashboard/ClientOnboardingForm'));
const LocalSEOLandingPage = lazy(() => import('@/components/LocalSEOLandingPage'));
const RVDealerProposal = lazy(() => import('@/components/RVDealerProposal'));
const DentalLocalSEO = lazy(() => import('@/components/DentalLocalSEO'));
const JobStreamDashboard = lazy(() => import('@/components/jobstream/JobStreamDashboard'));
const JobDispatchProLanding = lazy(() => import('@/components/jobstream/JobDispatchProLanding'));
const BookingWidget = lazy(() => import('@/components/jobstream/BookingWidget'));
const LocalBrandingProLanding = lazy(() => import('@/components/LocalBrandingProLanding'));
const BundlePricingPage = lazy(() => import('@/components/BundlePricingPage'));
const ClientPaymentPage = lazy(() => import('@/pages/ClientPaymentPage'));
const PublicBookingPage = lazy(() => import('@/pages/PublicBookingPage'));
const ProsperLanding = lazy(() => import('@/pages/ProsperLanding'));
const ProsperThankYou = lazy(() => import('@/pages/ProsperThankYou'));
const ReviewPage = lazy(() => import('@/pages/ReviewPage'));
const JobClipUpload = lazy(() => import('@/pages/JobClipUpload'));
const NationalCityLandingPage = lazy(() => import('@/components/locations/NationalCityLandingPage'));
const GeoServiceLandingPage = lazy(() => import('@/components/locations/GeoServiceLandingPage'));
const StateLandingPage = lazy(() => import('@/components/locations/StateLandingPage'));
const NationalLocationsPage = lazy(() => import('@/components/locations/NationalLocationsPage'));
const LocationRouter = lazy(() => import('@/components/locations/LocationRouter'));
import HeatmapTracker from '@/components/HeatmapTracker';
import VisitorIntelPixel from '@/components/VisitorIntelPixel';
const SpanishContactPage = lazy(() => import('@/components/spanish/SpanishContactPage'));
const SpanishMapDominator = lazy(() => import('@/components/spanish/SpanishMapDominator'));
const SpanishSocialMedia = lazy(() => import('@/components/spanish/SpanishSocialMedia'));
const SpanishPricing = lazy(() => import('@/components/spanish/SpanishPricing'));
const SpanishLocalBusiness = lazy(() => import('@/components/spanish/SpanishLocalBusiness'));
const SpanishBilingualAddon = lazy(() => import('@/components/spanish/SpanishBilingualAddon'));
const SpanishDealers = lazy(() => import('@/components/spanish/SpanishDealers'));
const SpanishAISearch = lazy(() => import('@/components/spanish/SpanishAISearch'));
const SpanishPersonalPortfolio = lazy(() => import('@/components/spanish/SpanishPersonalPortfolio'));
const SpanishEnterprise = lazy(() => import('@/components/spanish/SpanishEnterprise'));
const SpanishMobileApps = lazy(() => import('@/components/spanish/SpanishMobileApps'));
const SpanishAIVisibilityDemo = lazy(() => import('@/components/spanish/SpanishAIVisibilityDemo'));
const SpanishAboutUs = lazy(() => import('@/components/spanish/SpanishAboutUs'));
const SpanishFAQ = lazy(() => import('@/components/spanish/SpanishFAQ'));
const SpanishNewBusiness = lazy(() => import('@/components/spanish/SpanishNewBusiness'));
const SpanishPress = lazy(() => import('@/components/spanish/SpanishPress'));
const SpanishPrivacy = lazy(() => import('@/components/spanish/SpanishPrivacy'));
const SpanishTerms = lazy(() => import('@/components/spanish/SpanishTerms'));
const SpanishSMSTerms = lazy(() => import('@/components/spanish/SpanishSMSTerms'));

const FOOTER_EXCLUDE = ['/admin', '/partner/hq', '/affiliate/dashboard', '/client-portal', '/client', '/social-login', '/ppc-portal', '/social-tools', '/publisher', '/confirm-email', '/reupload', '/send-files', '/hosting', '/client-pay', '/pay/', '/superpixel-report', '/digital-marketing-platform', '/es/plataforma-marketing-digital', '/demo', '/jobstream', '/local-branding', '/bundle', '/stevesattler', '/book', '/agency/dashboard', '/redesign/', '/mockup/', '/proposal/', '/onboarding/', '/lsa/', '/prosper-garage-door', '/review/', '/site/', '/upload/'];

// Lightweight fallback shown while a lazy-loaded route chunk downloads.
function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="route-loading">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>
  );
}

// Prefetch route chunks on nav hover/focus so the next page is near-instant.
// webpack dedupes these dynamic imports against the lazy() chunks above, so the
// chunk is already cached by the time the user clicks through.
const prefetchServices = () => {
  import('@/components/LocalBusinessLanding');
  import('@/components/AISearchSection');
  import('@/components/SocialMediaLanding');
  import('@/components/CalendlyBooking');
  import('@/components/EnterpriseOnboarding');
  import('@/pages/DigitalMarketingPlatform');
};
const prefetchProducts = () => {
  import('@/components/MapDominatorLanding');
  import('@/components/PersonalPortfolioSection');
  import('@/components/GiftPortfolioLanding');
  import('@/components/CharityFundraiserLanding');
};
const prefetchBlog = () => { import('@/components/BlogList'); };


// External redirect component — sends users to standalone sites
function ExternalRedirect({ url }) {
  React.useEffect(() => { window.location.href = url; }, [url]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-slate-600">Redirecting you...</p>
      </div>
    </div>
  );
}

function GlobalFooter() {
  const { pathname } = useLocation();
  const hide = FOOTER_EXCLUDE.some(p => pathname.startsWith(p));
  if (hide) return null;
  return <Footer />;
}

// Redirect helper that preserves the query string (so ?ref=, ?code= etc. survive).
function RedirectWithQuery({ to }) {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search, hash: location.hash }} replace />;
}

// Smart /refer entrypoint:
//   • Signed-in partner → Partner HQ with the Warm-Intro modal pre-opened.
//   • Unauthenticated visitor (typically scanning a partner's QR business card
//     with ?p=PARTNERCODE) → home page tagged with ?ref=PARTNERCODE so the
//     existing referral attribution session-storage credits the partner when
//     the prospect later converts.
function ReferRoute() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const p = params.get('p');
  const isPartner = typeof window !== 'undefined' &&
    !!localStorage.getItem('affiliate_token') &&
    !!localStorage.getItem('affiliate_code');
  if (isPartner) {
    return <Navigate to={{ pathname: '/partner/hq', search: '?action=warm-intro' }} replace />;
  }
  if (p) {
    // Public prospect scanning a partner's QR card. Seed referral attribution.
    return <Navigate to={{ pathname: '/', search: `?ref=${encodeURIComponent(p)}&src=warm_qr` }} replace />;
  }
  // No partner code — bounce to the public Warm-Handoffs explainer.
  return <Navigate to={{ pathname: '/warm-handoffs' }} replace />;
}

// Partner-apply link that captures which page the user was on when they clicked.
// Routes to /affiliate with ?apply=1 so the Partner Application modal auto-opens
// on the canonical partner landing page (no dead-end forms on an unrelated /partner/apply page).
function BecomePartnerLink({ children, ...rest }) {
  const { pathname } = useLocation();
  const slug = pathname === '/' ? 'home' : pathname.replace(/^\//, '').split('/')[0].replace(/[^a-z0-9]/gi, '') || 'home';
  const isEs = pathname === '/es' || pathname.startsWith('/es/');
  // Attribution goes in sessionStorage, not the URL, so Google never crawls ?src= duplicates.
  const target = isEs ? '/es/socio/postular#apply' : '/affiliate#apply';
  const remember = (e) => {
    try { sessionStorage.setItem('wts_signup_source', `nav_${slug}`); } catch (err) {}
    if (rest.onClick) rest.onClick(e);
  };
  return <Link to={target} {...rest} onClick={remember}>{children}</Link>;
}

// Redirect www to non-www for SEO (canonical URL enforcement)
if (typeof window !== 'undefined' && window.location.hostname.startsWith('www.')) {
  window.location.href = window.location.href.replace('www.', '');
}


// ScrollToTop component - scrolls to top on route change, handles hash links
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  
  useEffect(() => {
    if (hash) {
      // If there's a hash, scroll to that element after a short delay
      setTimeout(() => {
        const element = document.getElementById(hash.replace('#', ''));
        if (element) {
          // Get the sticky header height (approximately 70px)
          const headerOffset = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 150);
    } else {
      // Otherwise scroll to top
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  
  return null;
}

// URL is the single source of truth for language:
//   /es/*  → i18n.language = 'es'
//   anything else → i18n.language = 'en'
// This eliminates the mixed-language flash that happens when the user toggles
// EN↔ES rapidly: without this, i18n.language could stay 'es' after the user
// navigated back to `/`, leaving Spanish strings ("Buscar una idea de regalo
// único...") leaking onto the English homepage.
function I18nRouteLock() {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const i18nLang = (i18n.language || 'en').split('-')[0];
  useEffect(() => {
    const isEs = pathname === '/es' || pathname.startsWith('/es/');
    const want = isEs ? 'es' : 'en';
    if (i18nLang !== want) {
      i18n.changeLanguage(want);
    }
  }, [pathname, i18nLang, i18n]);
  return null;
}

const NAV_EXCLUDE = ['/admin', '/hosting', '/client-portal', '/client', '/ppc-portal', '/pay/', '/client-pay', '/send-files', '/superpixel-report', '/demo', '/jobstream', '/local-branding', '/bundle', '/stevesattler', '/book', '/driver', '/es/conductor', '/portfolio/proposal', '/portfolio/tools', '/redesign/', '/ai-visibility', '/2026-ai-visibility-report', '/tools', '/mockup/', '/proposal/', '/onboarding/', '/lsa/', '/prosper-garage-door', '/review/', '/site/', '/upload/'];

// Additional paths where NO popups (exit-intent, SmartPopup, contest, etc.) should
// ever fire — typically authenticated dashboards or hand-raiser flows (someone
// already filling out an inquiry/checkout form is the wrong audience for a popup).
// NAV is still shown on these pages; we only suppress popups/widgets.
const POPUP_EXCLUDE = [
  ...NAV_EXCLUDE,
  '/partner/hq',
  '/partner/install',
  '/partner/',
  '/affiliate/dashboard',
  '/affiliate/login',
  '/affiliates/login',
  '/login',
  '/signin',
  '/findings/',
  '/network-audit/',
  '/onboarding/',
  '/onboard/',
  '/offer/',
  '/social-login',
  '/warm-handoffs',
  // Hand-raiser / inquiry / onboarding / checkout flows — popups distract from intent
  '/enterprise',
  '/es/empresas',
  '/contact',
  '/es/contacto',
  '/onboarding',
  '/winner-onboarding',
  '/runnerup-onboarding',
  '/gift-onboard',
  '/agency/register',
  '/pay/',
  '/domain-checkout',
  // Derby Tip Sheet landing pages (EN + ES) — keep clean for paid/SMS/email blasts
  '/2026-kentucky-derby-churchill-downs-race-card-tip-sheet',
  '/es/2026-kentucky-derby-pronosticos-ia',
  // Lone Star + Triple Crown tip sheet landing pages
  '/lone-star-park-grand-prairie-tip-sheet',
  '/es/hipodromo-lone-star-park-pronosticos-ia',
  '/2026-preakness-stakes-pimlico-tip-sheet',
  '/es/2026-preakness-stakes-pronosticos-ia',
  '/2026-belmont-stakes-saratoga-tip-sheet',
  '/es/2026-belmont-stakes-pronosticos-ia',
];

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <HelmetProvider>
    <BrowserRouter>
        <ScrollToTop />
        <I18nRouteLock />
        <RefAttributionCapture />
        <HreflangTags />
        <RouteSEO />
        <StructuredData />
        <SchemaStack />
        <HeatmapTracker />
        <VisitorIntelPixel />
        <AppContent mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
    </BrowserRouter>
    <Toaster position="top-right" richColors closeButton />
    </HelmetProvider>
  );
}

function AppContent({ mobileMenuOpen, setMobileMenuOpen }) {
  const { pathname } = useLocation();
  const { t } = useTranslation(['nav', 'common']);
  const popupsPaused = usePopupsPaused();
  // A signed-in partner should never see lead-gen popups anywhere (they're not
  // the popup's audience). Matches the affiliate_token + affiliate_code session
  // check used elsewhere in this file. Re-evaluated on every route change.
  const isPartnerLoggedIn =
    !!localStorage.getItem('affiliate_token') && !!localStorage.getItem('affiliate_code');
  const hideNav = NAV_EXCLUDE.some(p => pathname.startsWith(p));
  const hidePopups = popupsPaused || isPartnerLoggedIn || POPUP_EXCLUDE.some(p => pathname.startsWith(p));
  const hideWidgets = hideNav; // Also hide popups/widgets on admin/hosting pages

  // Begin polling the global popup-pause kill-switch (once, app-wide)
  useEffect(() => { startPopupPausePolling(); }, []);

  // Compress + shadow the sticky nav once the page is scrolled (premium feel).
  const [navScrolled, setNavScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // EN → ES path map so nav links on /es/* go to the Spanish landing pages.
  // Single source of truth lives in utils/languageRoutes.js — kept in sync there.
  const isEs = pathname === '/es' || pathname.startsWith('/es/');
  const lp = (to) => {
    if (!isEs || typeof to !== 'string' || !to.startsWith('/')) return to;
    if (to.startsWith('/es')) return to;
    // Preserve hash and query
    const hashIdx = to.indexOf('#');
    const queryIdx = to.indexOf('?');
    let base = to, suffix = '';
    if (hashIdx >= 0 || queryIdx >= 0) {
      const splitAt = hashIdx >= 0 && queryIdx >= 0 ? Math.min(hashIdx, queryIdx) : Math.max(hashIdx, queryIdx);
      base = to.slice(0, splitAt);
      suffix = to.slice(splitAt);
    }
    const mapped = EN_TO_ES_MAP[base];
    return (mapped || base) + suffix;
  };
  
  return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        {/* Contest Announcement Bar — disabled (contest on hold) */}
        {/* {!hideNav && <ContestAnnouncementBar />} */}
        
        {/* Navigation - hidden on admin, hosting, portal pages */}
        {!hideNav && (
        <nav className={`sticky top-0 z-50 bg-white/70 backdrop-blur-2xl backdrop-saturate-150 border-b transition-[box-shadow,border-color] duration-300 ${navScrolled ? 'border-slate-200/70 shadow-[0_8px_30px_rgba(15,23,42,0.06)]' : 'border-slate-200/40'}`}>
          <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-[padding] duration-300 ${navScrolled ? 'py-2' : 'py-3'}`}>
            <div className="flex justify-between items-center">
              {/* Logo */}
              <div className="flex items-center space-x-2 flex-shrink-0">
                <Link to={lp("/")} className="group flex items-center gap-2.5">
                  <span className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-sm shadow-blue-600/20 transition-transform duration-200 group-hover:scale-105">
                    <Code className="h-5 w-5" />
                  </span>
                  <span className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">
                    Web<span className="text-blue-600">-</span>TechServices
                  </span>
                </Link>
                <LanguageToggle className="hidden sm:inline-flex ml-3" />
              </div>
              
              {/* Mobile header actions — compact contact buttons + menu (replaces
                  the floating Text Us / Talk-with-us pills on tablet & phone) */}
              <div className="flex items-center gap-1 xl:hidden">
                <a
                  href="tel:+18172647476"
                  className="flex items-center gap-1.5 px-2.5 py-2 rounded-full text-blue-600 font-semibold text-sm hover:bg-blue-50 transition-colors whitespace-nowrap"
                  aria-label="Call us now at (817) 264-7476"
                  data-testid="mobile-header-call-btn"
                >
                  <Phone className="h-5 w-5" />
                  <span className="hidden min-[420px]:inline">(817) 264-7476</span>
                </a>
                <button
                  onClick={() => window.dispatchEvent(new Event('wts:open-textus'))}
                  className="p-2 rounded-full text-blue-600 hover:bg-blue-50 transition-colors"
                  aria-label="Text us"
                  data-testid="mobile-header-textus-btn"
                >
                  <MessageSquare className="h-5 w-5" />
                </button>
                <button
                  className="p-2 rounded-md hover:bg-gray-100"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-label="Toggle menu"
                >
                  {mobileMenuOpen ? (
                    <X className="h-6 w-6 text-gray-600" />
                  ) : (
                    <Menu className="h-6 w-6 text-gray-600" />
                  )}
                </button>
              </div>
              
              {/* Desktop Navigation */}
              <div className="hidden xl:flex items-center gap-1 2xl:gap-2 min-w-0">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="rounded-full text-xs xl:text-sm hover:bg-slate-100/80 hover:text-blue-600 transition-colors" data-testid="nav-services-btn" onMouseEnter={prefetchServices} onFocus={prefetchServices}>
                      {t('nav:services.label')}
                      <ChevronDown className="h-4 w-4 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <Link to={lp("/local-business")}>
                      <DropdownMenuItem className="cursor-pointer">
                        <Monitor className="h-4 w-4 mr-2 text-blue-600" />
                        <div>
                          <div className="font-medium">{t('nav:services.webDesign')}</div>
                          <div className="text-xs text-slate-500">{t('nav:services.webDesignDesc')}</div>
                        </div>
                      </DropdownMenuItem>
                    </Link>
                    <DropdownMenuSeparator />
                    <Link to={lp("/ai-search")}>
                      <DropdownMenuItem className="cursor-pointer">
                        <Search className="h-4 w-4 mr-2 text-green-600" />
                        <div>
                          <div className="font-medium">{t('nav:services.seo')}</div>
                          <div className="text-xs text-slate-500">{t('nav:services.seoDesc')}</div>
                        </div>
                      </DropdownMenuItem>
                    </Link>
                    <DropdownMenuSeparator />
                    <Link to={lp("/social-media-management")}>
                      <DropdownMenuItem className="cursor-pointer">
                        <Bot className="h-4 w-4 mr-2 text-purple-600" />
                        <div>
                          <div className="font-medium">{t('nav:services.social')}</div>
                          <div className="text-xs text-slate-500">{t('nav:services.socialDesc')}</div>
                        </div>
                      </DropdownMenuItem>
                    </Link>
                    <DropdownMenuSeparator />
                    <Link to={lp("/contact")}>
                      <DropdownMenuItem className="cursor-pointer">
                        <Sparkles className="h-4 w-4 mr-2 text-yellow-600" />
                        <div>
                          <div className="font-medium">{t('nav:services.consulting')}</div>
                          <div className="text-xs text-slate-500">{t('nav:services.consultingDesc')}</div>
                        </div>
                      </DropdownMenuItem>
                    </Link>
                    <DropdownMenuSeparator />
                    <Link to={lp("/enterprise")}>
                      <DropdownMenuItem className="cursor-pointer">
                        <Code className="h-4 w-4 mr-2 text-slate-600" />
                        <div>
                          <div className="font-medium">{t('nav:services.custom')}</div>
                          <div className="text-xs text-slate-500">{t('nav:services.customDesc')}</div>
                        </div>
                      </DropdownMenuItem>
                    </Link>
                    <DropdownMenuSeparator />
                    <Link to={lp("/digital-marketing-platform")}>
                      <DropdownMenuItem className="cursor-pointer">
                        <Building className="h-4 w-4 mr-2 text-cyan-600" />
                        <div>
                          <div className="font-medium">{t('nav:services.platform')}</div>
                          <div className="text-xs text-slate-500">{t('nav:services.platformDesc')}</div>
                        </div>
                      </DropdownMenuItem>
                    </Link>
                    <DropdownMenuSeparator />
                    <Link to={lp("/pricing#backlinks")}>
                      <DropdownMenuItem className="cursor-pointer">
                        <Link2 className="h-4 w-4 mr-2 text-orange-600" />
                        <div>
                          <div className="font-medium">{t('nav:services.backlinks')}</div>
                          <div className="text-xs text-slate-500">{t('nav:services.backlinksDesc')}</div>
                        </div>
                      </DropdownMenuItem>
                    </Link>
                    <DropdownMenuSeparator />
                    <a href={lp("/agency/register")}>
                      <DropdownMenuItem className="cursor-pointer bg-blue-50">
                        <Users className="h-4 w-4 mr-2 text-blue-600" />
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {t('nav:services.whiteLabel')}
                            <span className="text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded font-bold">{t('nav:badges.new')}</span>
                          </div>
                          <div className="text-xs text-slate-500">{t('nav:services.whiteLabelDesc')}</div>
                        </div>
                      </DropdownMenuItem>
                    </a>
                  </DropdownMenuContent>
                </DropdownMenu>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="rounded-full text-xs xl:text-sm hover:bg-slate-100/80 hover:text-blue-600 transition-colors" data-testid="nav-products-btn" onMouseEnter={prefetchProducts} onFocus={prefetchProducts}>
                      {t('nav:products.label')}
                      <ChevronDown className="h-4 w-4 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-[720px] p-4">
                    <div className="grid grid-cols-3 gap-5">
                      <div>
                        <div className="px-1 pb-2 text-xs font-semibold text-slate-600 uppercase tracking-wider">{t('nav:products.groupBusiness')}</div>
                        <Link to={lp("/dealers")}>
                          <DropdownMenuItem className="cursor-pointer">
                            <Car className="h-4 w-4 mr-2 text-blue-600 shrink-0" />
                            <span className="text-sm">{t('nav:products.dealers')}</span>
                          </DropdownMenuItem>
                        </Link>
                        <Link to={lp("/local-business")}>
                          <DropdownMenuItem className="cursor-pointer">
                            <Store className="h-4 w-4 mr-2 text-green-600 shrink-0" />
                            <span className="text-sm">{t('nav:products.localBusiness')}</span>
                          </DropdownMenuItem>
                        </Link>
                        <Link to={lp("/enterprise")}>
                          <DropdownMenuItem className="cursor-pointer">
                            <Building className="h-4 w-4 mr-2 text-slate-600 shrink-0" />
                            <span className="text-sm">{t('nav:products.enterprise')}</span>
                          </DropdownMenuItem>
                        </Link>
                        <Link to={lp("/mobile-apps")}>
                          <DropdownMenuItem className="cursor-pointer">
                            <Smartphone className="h-4 w-4 mr-2 text-purple-600 shrink-0" />
                            <span className="text-sm">{t('nav:products.mobileApps')}</span>
                          </DropdownMenuItem>
                        </Link>
                      </div>
                      <div>
                        <div className="px-1 pb-2 text-xs font-semibold text-slate-600 uppercase tracking-wider">{t('nav:products.groupAiMarketing')}</div>
                        <Link to={lp("/ai-visibility-demo")}>
                          <DropdownMenuItem className="cursor-pointer">
                            <Eye className="h-4 w-4 mr-2 text-blue-600 shrink-0" />
                            <span className="text-sm">{t('nav:products.aiVisibility')}</span>
                          </DropdownMenuItem>
                        </Link>
                        <Link to={lp("/ppc-audit")}>
                          <DropdownMenuItem className="cursor-pointer">
                            <DollarSign className="h-4 w-4 mr-2 text-orange-600 shrink-0" />
                            <span className="text-sm">{t('nav:products.ppcAudit')}</span>
                          </DropdownMenuItem>
                        </Link>
                        <Link to={lp("/seo-turbo")}>
                          <DropdownMenuItem className="cursor-pointer">
                            <Sparkles className="h-4 w-4 mr-2 text-yellow-600 shrink-0" />
                            <span className="text-sm">{t('nav:products.turboSeo')}</span>
                          </DropdownMenuItem>
                        </Link>
                        <Link to={lp("/map-dominator")}>
                          <DropdownMenuItem className="cursor-pointer bg-emerald-50">
                            <MapPin className="h-4 w-4 mr-2 text-emerald-600 shrink-0" />
                            <div>
                              <div className="text-sm font-medium flex items-center gap-2">
                                Map Dominator
                                <span className="text-[10px] bg-emerald-500 text-white px-1.5 py-0.5 rounded font-bold">NEW</span>
                              </div>
                              <div className="text-xs text-slate-500">Dominate every map platform</div>
                            </div>
                          </DropdownMenuItem>
                        </Link>
                      </div>
                      <div>
                        <div className="px-1 pb-2 text-xs font-semibold text-slate-600 uppercase tracking-wider">{t('nav:products.groupSpecialty')}</div>
                        <a href="https://jobdispatchpro.com" target="_blank" rel="noopener noreferrer">
                          <DropdownMenuItem className="cursor-pointer bg-yellow-50">
                            <Wrench className="h-4 w-4 mr-2 text-amber-600 shrink-0" />
                            <div>
                              <div className="text-sm font-medium flex items-center gap-2">
                                {t('nav:products.jobDispatchPro')}
                                <span className="text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded font-bold">{t('nav:badges.hot')}</span>
                              </div>
                              <div className="text-xs text-slate-500">{t('nav:products.jobDispatchProDesc')}</div>
                            </div>
                          </DropdownMenuItem>
                        </a>
                        <a href="https://localbrandingpro.com" target="_blank" rel="noopener noreferrer">
                          <DropdownMenuItem className="cursor-pointer bg-emerald-50">
                            <Store className="h-4 w-4 mr-2 text-emerald-600 shrink-0" />
                            <div>
                              <div className="text-sm font-medium flex items-center gap-2">
                                LocalBrandingPro
                                <span className="text-[10px] bg-emerald-500 text-white px-1.5 py-0.5 rounded font-bold">NEW</span>
                              </div>
                              <div className="text-xs text-slate-500">Complete local branding for small biz</div>
                            </div>
                          </DropdownMenuItem>
                        </a>
                        <Link to="/visitor-leads" data-testid="nav-visitor-leads">
                          <DropdownMenuItem className="cursor-pointer bg-indigo-50">
                            <Eye className="h-4 w-4 mr-2 text-indigo-600 shrink-0" />
                            <div>
                              <div className="text-sm font-medium flex items-center gap-2">
                                Visitor Re-Targeting
                                <span className="text-[10px] bg-indigo-600 text-white px-1.5 py-0.5 rounded font-bold">NEW</span>
                              </div>
                              <div className="text-xs text-slate-500">Identify anonymous visitors & auto-follow-up</div>
                            </div>
                          </DropdownMenuItem>
                        </Link>
                        <a href="https://charityfundraiser.com" target="_blank" rel="noopener noreferrer">
                          <DropdownMenuItem className="cursor-pointer bg-rose-50">
                            <Heart className="h-4 w-4 mr-2 text-rose-600 shrink-0" />
                            <div>
                              <div className="text-sm font-medium flex items-center gap-2">
                                CharityFundraiser
                                <span className="text-[10px] bg-rose-500 text-white px-1.5 py-0.5 rounded font-bold">NEW</span>
                              </div>
                              <div className="text-xs text-slate-500">All-in-one nonprofit fundraising platform</div>
                            </div>
                          </DropdownMenuItem>
                        </a>
                        <Link to={lp("/personal-portfolio")}>
                          <DropdownMenuItem className="cursor-pointer">
                            <Monitor className="h-4 w-4 mr-2 text-blue-600 shrink-0" />
                            <span className="text-sm">{t('nav:products.personalPortfolio')}</span>
                          </DropdownMenuItem>
                        </Link>
                        <Link to={lp("/gift-portfolio")}>
                          <DropdownMenuItem className="cursor-pointer">
                            <GraduationCap className="h-4 w-4 mr-2 text-blue-600 shrink-0" />
                            <span className="text-sm">{t('nav:products.giftPortfolio')}</span>
                          </DropdownMenuItem>
                        </Link>
                      </div>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="rounded-full text-xs xl:text-sm hover:bg-slate-100/80 hover:text-blue-600 transition-colors" data-testid="nav-industries-btn">
                      {t('nav:industries.label')}
                      <ChevronDown className="h-4 w-4 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-72">
                    <div className="px-2 py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider">{t('nav:industries.groupSeoPackages')}</div>
                    <Link to={lp("/dental-local-seo")}>
                      <DropdownMenuItem className="cursor-pointer bg-blue-50">
                        <Heart className="h-4 w-4 mr-2 text-teal-600" />
                        <div className="flex-1">
                          <div className="font-medium flex items-center gap-2">
                            {t('nav:industries.dental')}
                            <span className="text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded font-bold">{t('nav:badges.new')}</span>
                          </div>
                          <div className="text-xs text-slate-500">{t('nav:industries.dentalDesc')}</div>
                        </div>
                      </DropdownMenuItem>
                    </Link>
                    <Link to={lp("/rv-local-domination")}>
                      <DropdownMenuItem className="cursor-pointer bg-blue-50">
                        <Car className="h-4 w-4 mr-2 text-orange-600" />
                        <div className="flex-1">
                          <div className="font-medium flex items-center gap-2">
                            {t('nav:industries.rv')}
                            <span className="text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded font-bold">{t('nav:badges.new')}</span>
                          </div>
                          <div className="text-xs text-slate-500">{t('nav:industries.rvDesc')}</div>
                        </div>
                      </DropdownMenuItem>
                    </Link>
                    <DropdownMenuSeparator />
                    <div className="px-2 py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider">{t('nav:industries.groupWebsiteSolutions')}</div>
                    <Link to={lp("/dealers")}>
                      <DropdownMenuItem className="cursor-pointer">
                        <Car className="h-4 w-4 mr-2 text-blue-600" />
                        <div>
                          <div className="font-medium">{t('nav:industries.carDealers')}</div>
                          <div className="text-xs text-slate-500">{t('nav:industries.carDealersDesc')}</div>
                        </div>
                      </DropdownMenuItem>
                    </Link>
                    <Link to={lp("/local-business")}>
                      <DropdownMenuItem className="cursor-pointer">
                        <Store className="h-4 w-4 mr-2 text-green-600" />
                        <div>
                          <div className="font-medium">{t('nav:industries.local')}</div>
                          <div className="text-xs text-slate-500">{t('nav:industries.localDesc')}</div>
                        </div>
                      </DropdownMenuItem>
                    </Link>
                    <Link to={lp("/new-business")}>
                      <DropdownMenuItem className="cursor-pointer">
                        <Sparkles className="h-4 w-4 mr-2 text-yellow-600" />
                        <div>
                          <div className="font-medium">{t('nav:industries.newBusiness')}</div>
                          <div className="text-xs text-slate-500">{t('nav:industries.newBusinessDesc')}</div>
                        </div>
                      </DropdownMenuItem>
                    </Link>
                    <DropdownMenuSeparator />
                    <div className="px-2 py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider">{t('nav:industries.groupHomeServices')}</div>
                    <a href="https://jobdispatchpro.com" target="_blank" rel="noopener noreferrer">
                      <DropdownMenuItem className="cursor-pointer bg-amber-50">
                        <Droplets className="h-4 w-4 mr-2 text-blue-500" />
                        <div className="flex-1">
                          <div className="font-medium flex items-center gap-2">
                            {t('nav:industries.cleaning')}
                            <span className="text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded font-bold">{t('nav:badges.jdp')}</span>
                          </div>
                          <div className="text-xs text-slate-500">{t('nav:industries.cleaningDesc')}</div>
                        </div>
                      </DropdownMenuItem>
                    </a>
                    <a href="https://jobdispatchpro.com" target="_blank" rel="noopener noreferrer">
                      <DropdownMenuItem className="cursor-pointer bg-amber-50">
                        <Wrench className="h-4 w-4 mr-2 text-red-500" />
                        <div className="flex-1">
                          <div className="font-medium flex items-center gap-2">
                            {t('nav:industries.plumbing')}
                            <span className="text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded font-bold">{t('nav:badges.jdp')}</span>
                          </div>
                          <div className="text-xs text-slate-500">{t('nav:industries.plumbingDesc')}</div>
                        </div>
                      </DropdownMenuItem>
                    </a>
                    <a href="https://jobdispatchpro.com" target="_blank" rel="noopener noreferrer">
                      <DropdownMenuItem className="cursor-pointer bg-amber-50">
                        <Leaf className="h-4 w-4 mr-2 text-green-500" />
                        <div className="flex-1">
                          <div className="font-medium flex items-center gap-2">
                            {t('nav:industries.landscaping')}
                            <span className="text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded font-bold">{t('nav:badges.jdp')}</span>
                          </div>
                          <div className="text-xs text-slate-500">{t('nav:industries.landscapingDesc')}</div>
                        </div>
                      </DropdownMenuItem>
                    </a>
                    <a href="https://jobdispatchpro.com" target="_blank" rel="noopener noreferrer">
                      <DropdownMenuItem className="cursor-pointer bg-amber-50">
                        <Zap className="h-4 w-4 mr-2 text-yellow-500" />
                        <div className="flex-1">
                          <div className="font-medium flex items-center gap-2">
                            {t('nav:industries.electrician')}
                            <span className="text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded font-bold">{t('nav:badges.jdp')}</span>
                          </div>
                          <div className="text-xs text-slate-500">{t('nav:industries.electricianDesc')}</div>
                        </div>
                      </DropdownMenuItem>
                    </a>
                    <a href="https://jobdispatchpro.com" target="_blank" rel="noopener noreferrer">
                      <DropdownMenuItem className="cursor-pointer bg-amber-50">
                        <Bug className="h-4 w-4 mr-2 text-orange-600" />
                        <div className="flex-1">
                          <div className="font-medium flex items-center gap-2">
                            {t('nav:industries.pestControl')}
                            <span className="text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded font-bold">{t('nav:badges.jdp')}</span>
                          </div>
                          <div className="text-xs text-slate-500">{t('nav:industries.pestControlDesc')}</div>
                        </div>
                      </DropdownMenuItem>
                    </a>
                    <a href="https://jobdispatchpro.com" target="_blank" rel="noopener noreferrer">
                      <DropdownMenuItem className="cursor-pointer bg-amber-50">
                        <HardHat className="h-4 w-4 mr-2 text-slate-600" />
                        <div className="flex-1">
                          <div className="font-medium flex items-center gap-2">
                            {t('nav:industries.roofing')}
                            <span className="text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded font-bold">{t('nav:badges.jdp')}</span>
                          </div>
                          <div className="text-xs text-slate-500">{t('nav:industries.roofingDesc')}</div>
                        </div>
                      </DropdownMenuItem>
                    </a>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Link to={lp("/affiliate")} className="hidden nav-wide:inline-flex">
                  <Button variant="ghost" size="sm" className="text-slate-700 font-medium text-xs xl:text-sm" data-testid="nav-affiliate-btn">
                    {t('nav:primary.partnerProgram')}
                  </Button>
                </Link>
                <Link to={lp("/blog")} className="hidden nav-wide:inline-flex" onMouseEnter={prefetchBlog}>
                  <Button variant="ghost" size="sm" className="text-xs xl:text-sm" data-testid="nav-blog-btn">
                    {t('nav:primary.blog')}
                  </Button>
                </Link>
                {/* Contest nav link removed — contest on hold */}
                <Link to="/admin/login" className="hidden nav-wide:inline-flex">
                  <Button variant="ghost" size="sm" className="text-xs xl:text-sm" data-testid="nav-login-btn">
                    {t('nav:primary.login')}
                  </Button>
                </Link>
                <Link to="/client/login" className="hidden nav-wide:inline-flex">
                  <Button variant="ghost" size="sm" className="text-xs xl:text-sm text-cyan-600" data-testid="nav-client-login-btn">
                    {t('nav:primary.clientPortal')}
                  </Button>
                </Link>
                <a href={lp("/agency/register")} className="hidden nav-wide:inline-flex">
                  <Button variant="outline" size="sm" className="text-xs xl:text-sm border-blue-600 text-blue-600 hover:bg-blue-50" data-testid="nav-agency-btn">
                    {t('nav:primary.forAgencies')}
                  </Button>
                </a>
                <BecomePartnerLink>
                  <Button variant="outline" size="sm" className="text-xs xl:text-sm border-amber-500 text-amber-600 hover:bg-amber-50 font-semibold" data-testid="nav-become-partner-btn">
                    {t('nav:primary.becomePartner')}
                  </Button>
                </BecomePartnerLink>
                <a href="tel:+18172647476" data-testid="nav-phone-desktop" aria-label="Call Web-TechServices at (817) 264-7476" className="inline-flex items-center gap-1.5 px-2 text-sm font-semibold text-slate-900 hover:text-blue-600 transition-colors whitespace-nowrap">
                  <Phone className="h-4 w-4 text-blue-600" />
                  <span className="hidden nav-wide:inline">(817) 264-7476</span>
                </a>
                <Link to={lp("/contact")}>
                  <Button size="sm" className="rounded-full px-5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-sm shadow-blue-600/20 transition-colors" data-testid="nav-contact-btn">
                    {t('nav:primary.freeConsultation')}
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Mobile Navigation Menu */}
            {mobileMenuOpen && (
              <div className="xl:hidden mt-3 pb-4 border-t border-slate-200/70 pt-4 max-h-[calc(100vh-6rem)] overflow-y-auto">
                <div className="flex flex-col space-y-2">
                  {/* Always-clickable phone at top of mobile menu */}
                  <a href="tel:+18172647476" data-testid="mobile-menu-call-btn" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold">
                      <Phone className="h-4 w-4 mr-2" /> Call (817) 264-7476
                    </Button>
                  </a>
                  {/* Language toggle on mobile */}
                  <div className="px-3 pb-2">
                    <LanguageToggle />
                  </div>
                  {/* Industry SEO Packages - Highlighted */}
                  <div className="px-3 py-2">
                    <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider">{t('nav:mobile.industryPackages')}</p>
                  </div>
                  <Link to={lp("/dental-local-seo")} onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start bg-blue-50 text-blue-700">
                      <Heart className="h-4 w-4 mr-2 text-teal-600" />
                      {t('nav:industries.dental')}
                      <span className="ml-2 text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded font-bold">{t('nav:badges.new')}</span>
                    </Button>
                  </Link>
                  <Link to={lp("/rv-local-domination")} onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start bg-blue-50 text-blue-700">
                      <Car className="h-4 w-4 mr-2 text-orange-600" />
                      {t('nav:industries.rv')}
                      <span className="ml-2 text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded font-bold">{t('nav:badges.new')}</span>
                    </Button>
                  </Link>
                  
                  <div className="border-t border-slate-200 my-2"></div>
                  
                  <Link to={lp("/dealers")} onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start">
                      {t('nav:mobile.forCarRvDealers')}
                    </Button>
                  </Link>
                  <Link to={lp("/local-business")} onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start">
                      {t('nav:industries.local')}
                    </Button>
                  </Link>
                  <Link to={lp("/mobile-apps")} onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start">
                      {t('nav:products.mobileApps')}
                    </Button>
                  </Link>
                  <Link to={lp("/personal-portfolio")} onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start">
                      {t('nav:products.personalPortfolio')}
                    </Button>
                  </Link>
                  <Link to={lp("/gift-portfolio")} onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start pl-6">
                      <GraduationCap className="h-4 w-4 mr-2 text-blue-600" />
                      {t('nav:products.giftPortfolio')}
                    </Button>
                  </Link>
                  <Link to={lp("/enterprise")} onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start">
                      {t('nav:mobile.enterpriseSolutions')}
                    </Button>
                  </Link>
                  <Link to={lp("/digital-marketing-platform")} onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start">
                      <Building className="h-4 w-4 mr-2 text-cyan-600" />
                      {t('nav:mobile.digitalMarketingPlatform')}
                    </Button>
                  </Link>
                  <a href={lp("/agency/register")} onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start bg-blue-50 text-blue-700">
                      <Users className="h-4 w-4 mr-2 text-blue-600" />
                      {t('nav:services.whiteLabel')}
                      <span className="ml-2 text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded font-bold">{t('nav:badges.new')}</span>
                    </Button>
                  </a>

                  {/* Specialty Products */}
                  <div className="px-3 py-2">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Our Products</p>
                  </div>
                  <a href="https://jobdispatchpro.com" target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start bg-amber-50 text-amber-700">
                      <Wrench className="h-4 w-4 mr-2 text-amber-600" />
                      JobDispatchPro
                      <span className="ml-2 text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded font-bold">HOT</span>
                    </Button>
                  </a>
                  <a href="https://localbrandingpro.com" target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start bg-emerald-50 text-emerald-700">
                      <Store className="h-4 w-4 mr-2 text-emerald-600" />
                      LocalBrandingPro
                      <span className="ml-2 text-[10px] bg-emerald-500 text-white px-1.5 py-0.5 rounded font-bold">NEW</span>
                    </Button>
                  </a>
                  <a href="https://charityfundraiser.com" target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start bg-rose-50 text-rose-700">
                      <Heart className="h-4 w-4 mr-2 text-rose-600" />
                      CharityFundraiser
                      <span className="ml-2 text-[10px] bg-rose-500 text-white px-1.5 py-0.5 rounded font-bold">NEW</span>
                    </Button>
                  </a>
                  
                  {/* AI Visibility Section */}
                  <div className="px-3 py-2">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('nav:mobile.aiVisibility')}</p>
                  </div>
                  <Link to={lp("/ai-visibility-demo")} onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start pl-6">
                      <Eye className="h-4 w-4 mr-2 text-blue-600" />
                      {t('nav:mobile.interactiveDemo')}
                    </Button>
                  </Link>
                  <Link to={lp("/ai-search")} onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start pl-6">
                      <Bot className="h-4 w-4 mr-2 text-blue-600" />
                      {t('nav:mobile.aiSearchVisibility')}
                    </Button>
                  </Link>
                  <Link to={lp("/ai-visibility-check")} onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start pl-6">
                      <Search className="h-4 w-4 mr-2 text-green-600" />
                      {t('nav:mobile.freeVisibilityCheck')}
                    </Button>
                  </Link>
                  <Link to={lp("/ai-visibility-trial")} onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start pl-6">
                      <Sparkles className="h-4 w-4 mr-2 text-yellow-600" />
                      {t('nav:mobile.startFreeTrial')}
                    </Button>
                  </Link>
                  <Link to={lp("/ppc-audit")} onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start pl-6">
                      <DollarSign className="h-4 w-4 mr-2 text-orange-600" />
                      {t('nav:mobile.freePpcAudit')}
                    </Button>
                  </Link>
                  <Link to={lp("/map-dominator")} onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start pl-6">
                      <MapPin className="h-4 w-4 mr-2 text-emerald-600" />
                      Map Dominator
                    </Button>
                  </Link>
                  
                  <Link to={lp("/affiliate")} onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start text-slate-700 font-medium">
                      {t('nav:primary.partnerProgram')}
                    </Button>
                  </Link>
                  <BecomePartnerLink onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full justify-start border-amber-500 text-amber-600 hover:bg-amber-50 font-semibold" data-testid="mobile-nav-become-partner">
                      {t('nav:primary.becomePartner')}
                    </Button>
                  </BecomePartnerLink>
                  <Link to={lp("/blog")} onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start">
                      {t('nav:primary.blog')}
                    </Button>
                  </Link>
                  <Link to={lp("/press-releases")} onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start">
                      {t('nav:primary.pressReleases')}
                    </Button>
                  </Link>
                  {/* Contest mobile link removed — contest on hold */}
                  
                  <div className="border-t border-gray-200 pt-2 mt-2">
                    <Link to="/admin/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start">
                        {t('nav:primary.login')}
                      </Button>
                    </Link>
                    <Link to={lp("/contact")} onClick={() => setMobileMenuOpen(false)}>
                      <Button className="w-full mt-2 bg-blue-600 hover:bg-blue-700">
                        {t('common:getStarted')}
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </nav>
        )}

        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/es" element={<SpanishHome />} />
          <Route path="/es/directorio" element={<SpanishIndexPage />} />
          {/* Spanish Partner Funnel — full ES sister set of /partner/* */}
          <Route path="/es/socio" element={<SpanishPartnerLanding />} />
          <Route path="/es/socio/postular" element={<SpanishBecomePartnerPage />} />
          <Route path="/es/socio/terminos" element={<SpanishPartnerTerms />} />
          <Route path="/es/socio/portafolio" element={<SpanishPartnerPortfolio />} />
          {/* Spanish Blog — EN↔ES parity */}
          <Route path="/es/blog" element={<SpanishBlogList />} />
          <Route path="/es/blog/:slug" element={<SpanishBlogPost />} />
          {/* Map Dominator Phase 2 — PWA Driver app (EN + ES) */}
          <Route path="/driver" element={<DriverApp lang="en" />} />
          <Route path="/es/conductor" element={<DriverApp lang="es" />} />
          <Route path="/admin/driver-coverage" element={<AdminDriveCoverage />} />
          <Route path="/es/:slug" element={<SpanishLandingPage />} />
          <Route path="/bilingual-addon" element={<BilingualAddonLanding />} />
          <Route path="/bilingual-addon/success" element={<BilingualAddonLanding />} />
          <Route path="/2026-kentucky-derby-churchill-downs-race-card-tip-sheet" element={<DerbyTipsheetLanding />} />
          <Route path="/es/2026-kentucky-derby-pronosticos-ia" element={<DerbyTipsheetSpanish />} />
          {/* Short branded URLs for social/SMS sharing — redirect to canonical landing pages */}
          <Route path="/free-derby-tipsheet" element={<Navigate to="/2026-kentucky-derby-churchill-downs-race-card-tip-sheet" replace />} />
          <Route path="/free-derby-tipsheet/es" element={<Navigate to="/es/2026-kentucky-derby-pronosticos-ia" replace />} />
          <Route path="/lone-star-park-grand-prairie-tip-sheet" element={<LonestarTipsheetLanding />} />
          <Route path="/es/hipodromo-lone-star-park-pronosticos-ia" element={<LonestarTipsheetSpanish />} />
          <Route path="/2026-preakness-stakes-pimlico-tip-sheet" element={<PreaknessTipsheetLanding />} />
          <Route path="/es/2026-preakness-stakes-pronosticos-ia" element={<PreaknessTipsheetSpanish />} />
          <Route path="/2026-belmont-stakes-saratoga-tip-sheet" element={<BelmontTipsheetLanding />} />
          <Route path="/es/2026-belmont-stakes-pronosticos-ia" element={<BelmontTipsheetSpanish />} />
          <Route path="/2026-kentucky-derby-results" element={<ResultsPage event="derby" lang="en" />} />
          <Route path="/lone-star-park-results" element={<ResultsPage event="lonestar" lang="en" />} />
          <Route path="/es/2026-kentucky-derby-resultados" element={<ResultsPage event="derby" lang="es" />} />
          <Route path="/es/lone-star-park-resultados" element={<ResultsPage event="lonestar" lang="es" />} />
          {/* Short branded URLs for social/SMS sharing */}
          <Route path="/free-preakness-tipsheet" element={<Navigate to="/2026-preakness-stakes-pimlico-tip-sheet" replace />} />
          <Route path="/free-preakness-tipsheet/es" element={<Navigate to="/es/2026-preakness-stakes-pronosticos-ia" replace />} />
          <Route path="/free-belmont-tipsheet" element={<Navigate to="/2026-belmont-stakes-saratoga-tip-sheet" replace />} />
          <Route path="/free-belmont-tipsheet/es" element={<Navigate to="/es/2026-belmont-stakes-pronosticos-ia" replace />} />
          <Route path="/map-dominator" element={<MapDominatorLanding />} />
          <Route path="/map-dominator/success" element={<MapDominatorLanding />} />
          <Route path="/charity-fundraiser" element={<CharityFundraiserLanding />} />
          <Route path="/proposal/:slug" element={<ProposalPage />} />
          <Route path="/lsa/client/:token" element={<ClientLsaDashboard />} />
          <Route path="/lsa/welcome/:token" element={<LsaWelcome />} />
          <Route path="/social-onboarding/:slug" element={<SocialOnboarding />} />
          <Route path="/charityfundraiser" element={<CharityFundraiserLanding />} />
          <Route path="/intake" element={<ClientIntake />} />
          <Route path="/map-scan" element={<MapScanWizard />} />
          <Route path="/map-scan/success" element={<MapScanWizard />} />
          <Route path="/es/map-scan" element={<SpanishMapScan />} />
          {/* Spanish Tier 1 — main conversion pages */}
          <Route path="/es/contacto" element={<SpanishContactPage />} />
          <Route path="/es/mapa-dominador" element={<SpanishMapDominator />} />
          <Route path="/es/redes-sociales" element={<SpanishSocialMedia />} />
          <Route path="/es/precios" element={<SpanishPricing />} />
          <Route path="/es/negocio-local" element={<SpanishLocalBusiness />} />
          <Route path="/es/complemento-bilingue" element={<SpanishBilingualAddon />} />
          {/* Spanish Tier 2 — discovery/product pages */}
          <Route path="/es/concesionarios" element={<SpanishDealers />} />
          <Route path="/es/busqueda-ai" element={<SpanishAISearch />} />
          <Route path="/es/portafolio-personal" element={<SpanishPersonalPortfolio />} />
          <Route path="/es/empresas" element={<SpanishEnterprise />} />
          <Route path="/es/aplicaciones-moviles" element={<SpanishMobileApps />} />
          <Route path="/es/demo-visibilidad-ai" element={<SpanishAIVisibilityDemo />} />
          {/* Spanish Tier 3 — about, FAQ, new-business, press, legal pages */}
          <Route path="/es/sobre-nosotros" element={<SpanishAboutUs />} />
          <Route path="/es/preguntas-frecuentes" element={<SpanishFAQ />} />
          <Route path="/es/nuevo-negocio" element={<SpanishNewBusiness />} />
          <Route path="/es/prensa" element={<SpanishPress />} />
          <Route path="/es/privacidad" element={<SpanishPrivacy />} />
          <Route path="/es/terminos" element={<SpanishTerms />} />
          <Route path="/es/sms-terminos" element={<SpanishSMSTerms />} />
          <Route path="/warm-handoffs" element={<WarmHandoffsLanding />} />
          <Route path="/onboarding/data-access/:leadId" element={<DataAccessOnboarding />} />
          <Route path="/support/ticket/:number" element={<PublicTicketPage />} />
          <Route path="/local-business" element={<LocalBusinessLanding />} />
          <Route path="/seo-turbo" element={<SEOTurboLanding />} />
          <Route path="/seo-turbo/success" element={<SEOTurboSuccess />} />
          <Route path="/seo-packages" element={<SEOTurboLanding />} />
          <Route path="/seo-turbo/dashboard/:clientId" element={<SEODashboard />} />
          <Route path="/dealers" element={<DealerLandingPage />} />
          <Route path="/clauctions" element={<CLAuctionsLanding />} />
          <Route path="/mobile-apps" element={<MobileAppsSection />} />
          <Route path="/personal-portfolio" element={<PersonalPortfolioSection />} />
          <Route path="/personal-portfolio/success" element={<PortfolioSuccess />} />
          <Route path="/portfolio/proposal/:proposalId" element={<ProposalView />} />
          <Route path="/portfolio/tools" element={<PortfolioOwnerDashboard />} />
          <Route path="/domain-success" element={<DomainSuccess />} />
          <Route path="/ai-search" element={<AISearchSection />} />
          <Route path="/ai-search-audit" element={<AiSearchAudit />} />
          <Route path="/ai-visibility-trial" element={<AIVisibilityTrial />} />
          <Route path="/ai-visibility/success" element={<AIVisibilitySuccess />} />
          <Route path="/ai-visibility-check" element={<AIVisibilityCheckerPage />} />
          <Route path="/ai-visibility-demo" element={<AIVisibilityDemo />} />
          <Route path="/ai-visibility" element={<AIVisibilityDemo />} />
          <Route path="/2026-ai-visibility-report" element={<AIVisibilityReport2026 />} />
          <Route path="/ai-check" element={<AIVisibilityCheckerPage />} />
          <Route path="/contest" element={<ContestEndedPage />} />
          {/* Active contest pages redirected to ended page — contest on hold */}
          <Route path="/contest-social" element={<ContestEndedPage />} />
          <Route path="/contest-social/help-win" element={<ContestEndedPage />} />
          <Route path="/contest/vote" element={<ContestEndedPage />} />
          <Route path="/contest/verify" element={<ContestEndedPage />} />
          <Route path="/contest/gift" element={<ContestEndedPage />} />
          <Route path="/contest/share-tools" element={<ContestEndedPage />} />
          <Route path="/contest/grand-prize" element={<ContestEndedPage />} />
          <Route path="/contest/winners" element={<ContestWinnersPage />} />
          <Route path="/contest/thank-you" element={<ContestThankYou />} />
          <Route path="/winners" element={<ContestWinnersPage />} />
          <Route path="/winner-onboarding" element={<WinnerOnboardingForm />} />
          <Route path="/runnerup-onboarding" element={<RunnerUpOnboardingForm />} />
          <Route path="/reupload" element={<FileReupload />} />
          <Route path="/send-files" element={<ProspectFileUpload />} />
          <Route path="/enterprise" element={<EnterpriseOnboarding />} />
          <Route path="/client-credentials/:slug" element={<ClientCredentialsForm />} />
          <Route path="/onboarding/payment-success" element={<ServicePaymentSuccess />} />
          <Route path="/onboarding/:serviceType" element={<ServiceOnboarding />} />
          <Route path="/packages" element={<PricingPackages />} />
          <Route path="/seo-intelligence" element={<SEOIntelligenceLanding />} />
          <Route path="/pricing" element={<PricingPackages />} />
          <Route path="/tools" element={<ToolsPortal />} />
          <Route path="/services" element={<PricingPackages />} />
          <Route path="/web-design-services" element={<WebDesignServicesPage />} />
          <Route path="/website-analysis" element={<WebsiteAnalysisPage />} />
          <Route path="/new-business" element={<NewBusinessOffer />} />
          <Route path="/new-business-offer" element={<NewBusinessOffer />} />
          <Route path="/startup" element={<NewBusinessOffer />} />
          <Route path="/contact" element={<CalendlyBooking />} />
          <Route path="/affiliate" element={<AffiliateLanding />} />
          <Route path="/affiliate-landing" element={<AffiliateLanding />} />
          <Route path="/partner-landing" element={<AffiliateLanding />} />
          {/* Canonical partner URLs — these are what we promote going forward */}
          <Route path="/partner/apply" element={<BecomeAffiliatePage />} />
          <Route path="/partner/hq" element={<AffiliateDashboard />} />
          <Route path="/affiliate/sister-sites/:siteSlug" element={<SisterSiteMarketingKit />} />
          <Route path="/refer/:partnerCode/:siteSlug" element={<SisterSitePartnerLanding />} />
          <Route path="/affiliate/autopost-setup" element={<AutoPostSetupGuide />} />
          <Route path="/affiliate/linkedin/queue" element={<PartnerLinkedInQueue />} />
          <Route path="/partner/linkedin/queue" element={<PartnerLinkedInQueue />} />
          <Route path="/partner/missed/:code" element={<PartnerMissedEmails />} />
          <Route path="/findings/:token" element={<SecureFindingsPage />} />
          <Route path="/network-audit/:token" element={<NetworkAuditPage />} />
          <Route path="/onboard/:token" element={<OnboardingPage />} />
          <Route path="/offer/:token" element={<OfferPage />} />
          <Route path="/p/:token" element={<StandaloneProposalPage />} />
          <Route path="/dont-scan" element={<DontScanLanding />} />
          <Route path="/dont-scan/:partnerCode" element={<DontScanLanding />} />
          <Route path="/partner/install" element={<PartnerInstallLanding />} />
          {/* Back-compat 301s — preserve query string so ?ref=bcrecruit, ?code=... still work */}
          <Route path="/become-affiliate" element={<RedirectWithQuery to={lp("/partner/apply")} />} />
          <Route path="/join-affiliate" element={<RedirectWithQuery to={lp("/partner/apply")} />} />
          <Route path="/affiliate/portfolio" element={<AffiliatePortfolioLanding />} />
          <Route path="/partner/portfolio" element={<AffiliatePortfolioLanding />} />
          <Route path="/creator/:username" element={<InfluencerVideoCard />} />
          <Route path="/influencer/:username" element={<InfluencerVideoCard />} />
          <Route path="/partner" element={<AffiliateSimpleLanding />} />
          <Route path="/become-a-partner" element={<AffiliateSimpleLanding />} />
          <Route path="/creator" element={<InfluencerLanding />} />
          <Route path="/influencer" element={<InfluencerLanding />} />
          <Route path="/creator-partner" element={<InfluencerLanding />} />
          <Route path="/affiliate/status" element={<AffiliateStatus />} />
          <Route path="/partner/status" element={<AffiliateStatus />} />
          <Route path="/affiliate/dashboard" element={<RedirectWithQuery to="/partner/hq" />} />
          <Route path="/affiliates/login" element={<RedirectWithQuery to="/partner/hq" />} />
          <Route path="/affiliate/login" element={<RedirectWithQuery to="/partner/hq" />} />
          {/* Phone-bookmark friendly short URL: /refer
                • Signed-in partner → Partner HQ with the Warm-Intro modal.
                • Unauthenticated visitor with ?p=PARTNERCODE (QR card scan) →
                  home + ?ref=PARTNERCODE for attribution.
                • No code → public Warm-Handoffs explainer. */}
          <Route path="/refer" element={<ReferRoute />} />
          <Route path="/wh" element={<ReferRoute />} />
          <Route path="/warm-intro" element={<ReferRoute />} />
          {/* Tactical playbook — partners pull it up on their phone next to a prospect. */}
          <Route path="/playbook" element={<WarmHandoffPlaybook />} />
          <Route path="/refer/playbook" element={<WarmHandoffPlaybook />} />
          <Route path="/affiliate/media-kit" element={<MediaKit />} />
          <Route path="/partner/media-kit" element={<MediaKit />} />
          <Route path="/super-affiliate" element={<SuperAffiliateLanding />} />
          <Route path="/super-affiliate/apply" element={<SuperAffiliateLanding />} />
          <Route path="/ultra-affiliate" element={<SuperAffiliateLanding />} />
          <Route path="/ultra-affiliate/apply" element={<SuperAffiliateLanding />} />
          <Route path="/ultra-affiliate/dashboard/:ultraCode" element={<UltraAffiliateDashboard />} />
          {/* Top Tier Partner aliases (consistent partner branding) */}
          <Route path="/top-tier-partner" element={<SuperAffiliateLanding />} />
          <Route path="/top-tier-partner/apply" element={<SuperAffiliateLanding />} />
          <Route path="/top-tier-partner/dashboard/:ultraCode" element={<UltraAffiliateDashboard />} />
          <Route path="/affiliate/leaderboard" element={<AffiliateLeaderboard />} />
          <Route path="/partner/leaderboard" element={<AffiliateLeaderboard />} />
          <Route path="/client-portal/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/client-portal/login" element={<ClientPortalLogin />} />
          <Route path="/client-portal/:accountId/team" element={<ClientPortalTeam />} />
          <Route path="/partners" element={<PartnersLandingPage />} />
          <Route path="/agency-api" element={<PartnersLandingPage />} />
          <Route path="/white-label-api" element={<PartnersLandingPage />} />
          <Route path="/wts-on-steroids" element={<SuperAffiliateLanding />} />
          {/* Affiliate Promo Landing Pages */}
          <Route path="/p/:affiliateCode" element={<AffiliatePromoPage />} />
          <Route path="/p/:affiliateCode/:template" element={<AffiliatePromoPage />} />
          {/* Affiliate Recruitment Landing Pages */}
          <Route path="/r/:affiliateCode" element={<AffiliateRecruitPage />} />
          <Route path="/r/:affiliateCode/:template" element={<AffiliateRecruitPage />} />
          <Route path="/consultation" element={<ConsultationSimple />} />
          <Route path="/book-consultation" element={<CalendlyBooking />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/social" element={<SocialMediaHub />} />
          <Route path="/social-media" element={<SocialMediaHub />} />
          <Route path="/social-media-management" element={<SocialMediaLanding />} />
          <Route path="/social-media-pricing" element={<SocialMediaPricing />} />
          <Route path="/social-pricing" element={<SocialMediaPricing />} />
          <Route path="/client-portal" element={<ClientSocialPortal />} />
          <Route path="/social-login" element={<ClientSocialPortal />} />
          <Route path="/gift-onboard/:token" element={<GiftOnboardPage />} />
          
          {/* White-Label Agency */}
          <Route path="/agency/register" element={<AgencyRegisterPage />} />
          <Route path="/agency/dashboard" element={<AgencyDashboard />} />
          <Route path="/agency/portal" element={<AgencyClientPortal />} />
          <Route path="/agency/scan/:agencyCode" element={<AgencyScanner />} />
          <Route path="/gift-portfolio" element={<GiftPortfolioLanding />} />
          <Route path="/graduation-gift" element={<GiftPortfolioLanding />} />
          <Route path="/gift" element={<GiftPortfolioLanding />} />
          <Route path="/press" element={<PressPage />} />
          <Route path="/campaign-websites" element={<CampaignWebsitesPage />} />
          <Route path="/press-releases" element={<PressReleaseArchive />} />
          <Route path="/press-releases/:id" element={<PressReleaseDetail />} />
          {/* National Locations Directory */}
          <Route path="/locations" element={<NationalLocationsPage />} />
          
          <Route path="/locations/dallas-fort-worth" element={<DallasFortWorth />} />
          <Route path="/locations/austin" element={<Austin />} />
          <Route path="/locations/houston" element={<Houston />} />
          <Route path="/locations/san-antonio" element={<SanAntonio />} />
          <Route path="/locations/arlington" element={<Arlington />} />
          
          {/* National State+City Landing Pages - All 50 States */}
          <Route path="/locations/:stateSlug/:citySlug" element={<GeoRedirectGuard><NationalCityLandingPage /></GeoRedirectGuard>} />

          {/* Auto-generated City × Service SEO pages (Geo SEO Pipeline) */}
          <Route path="/locations/:stateSlug/:citySlug/:serviceId" element={<GeoRedirectGuard><GeoServiceLandingPage /></GeoRedirectGuard>} />
          
          {/* DFW City Pages OR State Landing Pages (LocationRouter handles both) */}
          <Route path="/locations/:citySlug" element={<LocationRouter />} />
          
          {/* Dynamic Service-Location Landing Pages - Maximum SEO Coverage */}
          <Route path="/services/local-seo-domination" element={<LocalSEOLandingPage />} />
          <Route path="/rv-local-domination" element={<RVDealerProposal />} />
          <Route path="/dental-local-seo" element={<DentalLocalSEO />} />
          <Route path="/services/:slug" element={<ServiceLocationPage />} />
          
          {/* Industry-specific SEO landing pages */}
          <Route path="/industries/dental-implants-fort-worth-tx" element={<DentalImplantsSEOFortWorth />} />
          <Route path="/dental-implants-fort-worth-tx" element={<DentalImplantsSEOFortWorth />} />
          <Route path="/fort-worth-tx-dental-implants" element={<DentalImplantsSEOFortWorth />} />
          
          {/* Legacy Service Pages - Arlington SEO Keywords */}
          <Route path="/services/web-design-arlington" element={<WebDesignArlington />} />
          <Route path="/services/seo-arlington" element={<SEOArlington />} />
          <Route path="/services/ecommerce-arlington" element={<EcommerceArlington />} />
          
          {/* SEO-friendly URL aliases */}
          <Route path="/arlington-web-design" element={<WebDesignArlington />} />
          <Route path="/web-design-arlington-tx" element={<WebDesignArlington />} />
          <Route path="/arlington-seo" element={<SEOArlington />} />
          <Route path="/seo-arlington-tx" element={<SEOArlington />} />
          
          <Route path="/seo-automation-package" element={<SEOPackagePage />} />
          <Route path="/confirm-email" element={<EmailConfirmation />} />
          <Route path="/go" element={<GoLinks />} />
          
          {/* Admin Routes */}
          <Route path="/login" element={<Navigate to="/admin/login" replace />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/seo-autopilot" element={<SeoAutopilotConsole />} />
          <Route path="/admin/haro" element={<AdminHaroAssistant />} />
          <Route path="/admin/password-reset" element={<AdminPasswordReset />} />
          <Route path="/admin/unlock-account" element={<UnlockAccount />} />
          
          {/* Client Dashboard */}
          <Route path="/client/login" element={<ClientLogin />} />
          <Route path="/client/login/:slug" element={<ClientLogin />} />
          <Route path="/client/dashboard" element={<ClientDashboard />} />
          <Route path="/client/payment-success" element={<ClientDashboard />} />
          <Route path="/client/onboarding/:accessCode" element={<ClientOnboardingForm />} />
          <Route path="/client/onboarding" element={<ClientOnboardingForm />} />
          <Route path="/digital-marketing-platform" element={<DigitalMarketingPlatform />} />
          <Route path="/es/plataforma-marketing-digital" element={<DigitalMarketingPlatform lang="es" />} />
          <Route path="/demo" element={<MockDemo />} />
          
          {/* Affiliate Routes (legacy) + Partner aliases */}
          <Route path="/affiliate/unlock-account" element={<UnlockAccount />} />
          <Route path="/partner/unlock-account" element={<UnlockAccount />} />
          <Route path="/affiliate/password-reset" element={<AffiliatePasswordReset />} />
          <Route path="/partner/password-reset" element={<AffiliatePasswordReset />} />
          
          {/* About Us Page */}
          <Route path="/about" element={<AboutUs />} />
          <Route path="/videos" element={<VideoGallery />} />
          <Route path="/ec-academy" element={<EcAcademy />} />
          <Route path="/academy" element={<EcAcademy />} />
          <Route path="/backlinks" element={<BacklinksPage />} />
          <Route path="/seo-authority" element={<BacklinksPage />} />
          <Route path="/offer/visitor-id" element={<VisitorIdOffer />} />
          <Route path="/redesign/:id" element={<RedesignMockupView />} />
          <Route path="/site/:slug" element={<SitePage />} />
          <Route path="/site/:slug/launch" element={<SiteLaunchPage />} />
          <Route path="/mockup/lr-boston" element={<CodeGate slug="lr-boston"><LrBostonMockup /></CodeGate>} />
          <Route path="/proposal/lr-boston" element={<CodeGate slug="lr-boston"><LrBostonProposal /></CodeGate>} />
          <Route path="/visitor-leads" element={<VisitorLeadsLanding />} />
          <Route path="/visitor-retargeting" element={<Navigate to="/visitor-leads" replace />} />
          <Route path="/portfolio" element={<PortfolioPage />} />
          
          {/* Affiliate Terms (legacy) + Partner alias */}
          <Route path="/affiliate/terms" element={<AffiliateTerms />} />
          <Route path="/partner/terms" element={<AffiliateTerms />} />
          
          {/* Privacy Policy */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          
          {/* Terms of Service */}
          <Route path="/terms" element={<TermsOfService />} />
          
          {/* SMS Terms & Conditions */}
          <Route path="/sms-terms" element={<SMSTerms />} />
          
          {/* Google Ads API Terms */}
          <Route path="/google-ads-api-terms" element={<GoogleAdsAPITerms />} />
          
          {/* PPC Audit Tool */}
          <Route path="/ppc-audit" element={<PPCAuditPage />} />
          
          {/* PPC Management Sales Funnel */}
          <Route path="/ppc-management" element={<PPCManagementFunnel />} />
          <Route path="/ppc-portal" element={<PPCClientPortal />} />
          
          {/* Meta App Review Accelerator */}
          <Route path="/meta-app-review" element={<MetaAppReviewAccelerator />} />
          <Route path="/meta-app-review/tool" element={<MetaReviewTool />} />
          
          {/* Meta Review Portal - Standalone Social Publisher for Meta App Review */}
          <Route path="/social-tools" element={<MetaReviewPortal />} />
          <Route path="/publisher" element={<MetaReviewPortal />} />
          
          {/* FAQ Page - LLM Optimized */}
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/es/faq" element={<FAQPage lang="es" />} />
          
          {/* Review Us Page */}
          <Route path="/review-us" element={<ReviewUsPage />} />
          <Route path="/reviews" element={<ReviewUsPage />} />
          
          {/* Client Payment Pages */}
          <Route path="/pay/:slug" element={<DynamicPaymentPage />} />
          <Route path="/superpixel-report/:siteId" element={<SuperpixelReport />} />
          <Route path="/seo-report/:id" element={<SeoAuditReport />} />
          <Route path="/client-pay/:slug" element={<DynamicPaymentPage />} />
          <Route path="/hosting/pay/:slug" element={<HostingPaymentPage />} />
          <Route path="/hosting/success" element={<HostingPaymentPage />} />
          
          {/* LocalBranding Pro - Redirects to standalone site */}
          <Route path="/local-branding" element={<ExternalRedirect url="https://localbrandingpro.com" />} />
          <Route path="/es/local-branding" element={<ExternalRedirect url="https://localbrandingpro.com" />} />
          <Route path="/es/regalo-portafolio" element={<GiftPortfolioLanding lang="es" />} />

          {/* Bundle Pricing - Redirects to JDP standalone site */}
          <Route path="/bundle" element={<ExternalRedirect url="https://jobdispatchpro.com/#pricing" />} />
          
          {/* Job Dispatch Pro - Redirects to standalone site, keeps dashboard for existing users */}
          <Route path="/jobstream" element={<ExternalRedirect url="https://jobdispatchpro.com" />} />
          <Route path="/jobstream/app" element={<JobStreamDashboard />} />
          <Route path="/jobstream/embed" element={<BookingWidget />} />
          
          {/* Private Client Payment Pages */}
          <Route path="/stevesattler" element={<ClientPaymentPage />} />

          {/* Public Booking Pages (Job Dispatch Pro) */}
          <Route path="/book/:slug" element={<PublicBookingPage />} />

          {/* Prosper Garage Door — Google Ads PPC landing page */}
          <Route path="/prosper-garage-door" element={<ProsperLanding />} />
          <Route path="/prosper-garage-door/thank-you" element={<ProsperThankYou />} />
          <Route path="/prosper-garage-door/review" element={<ReviewPage forcedSlug="prosper" />} />
          <Route path="/review/:slug" element={<ReviewPage />} />
          <Route path="/upload/:code" element={<JobClipUpload />} />
          
          {/* Legacy URL Redirects — Fix 404s in Google Search Console */}
          <Route path="/sitemap.php" element={<ExternalRedirect url="https://web-techservices.com/sitemap.xml" />} />
          <Route path="/dealerships/*" element={<ExternalRedirect url="https://web-techservices.com/" />} />
          <Route path="/index.php" element={<ExternalRedirect url="https://web-techservices.com/" />} />
          <Route path="/contact.php" element={<ExternalRedirect url="https://web-techservices.com/book-consultation" />} />
          <Route path="/about.php" element={<ExternalRedirect url="https://web-techservices.com/about" />} />
          <Route path="/services.php" element={<ExternalRedirect url="https://web-techservices.com/services" />} />
          <Route path="/blog.php" element={<ExternalRedirect url="https://web-techservices.com/ai-seo-news" />} />
          <Route path="/portfolio.php" element={<ExternalRedirect url="https://web-techservices.com/#portfolio" />} />
          
          {/* 404 Catch-All - Must be last */}
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
        
        {/* Global Footer - shows on all public pages, hidden on admin/dashboard/portal */}
        <GlobalFooter />
        
        {/* AI Chat Widget - hidden on admin/hosting/portal/jobstream pages */}
        {!hideWidgets && <AIChatWidget />}
        
        {/* Admin Floating Button - Shows when admin is logged in, hidden on jobstream */}
        {!hideWidgets && <AdminFloatingButton />}
        
        {/* Text Us Widget - hidden on admin/hosting/portal pages */}
        {!hideWidgets && <TextUsWidget />}

        {/* One-time Spanish language prompt for es-* browsers (public pages only) */}
        {!hideWidgets && <LanguagePrompt />}
        
        {/* Admin-managed popup (AI-built, database-driven) takes priority when active.
            When no admin popup is active, it silently yields to the legacy date-gated popups below. */}
        {!hidePopups && <DynamicPopup />}
        {!hidePopups && <CallbackWidget />}

        {/* Graduation Gift Portfolio season — SmartPopup is active through May 31, 2026.
            ExitIntentPopup auto-takes-over on June 1, 2026 (see date gate below).
            Single-popup policy preserved: only one shows at a time based on the date. */}
        {!hidePopups && new Date() < new Date('2026-06-01T00:00:00-05:00') && <SmartPopup />}

        {/* Exit-Intent Popup - hidden on dashboards (blocks tours/UI).
            Date-gated OFF during Graduation Gift season (May 2026). Takes over on Jun 1. */}
        {!hidePopups && new Date() >= new Date('2026-06-01T00:00:00-05:00') && <ExitIntentPopup />}
        
        {/* Contest Entry Popup - shown on public pages for new visitors */}
        {/* Contest popup disabled — contest on hold per user, April 18, 2026 */}
        {/* {!hideWidgets && <ContestEntryPopup />} */}
        
        </div>
  );
}

export default App;